import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/dashboard/PageHeader';
import ClockCard from '@/components/clock/ClockCard';
import TodayLog from '@/components/clock/TodayLog';
import HistoryTable from '@/components/clock/HistoryTable';
import JobLaborSummary from '@/components/clock/JobLaborSummary';
import WeeklySummary from '@/components/clock/WeeklySummary';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, RefreshCw, Users } from 'lucide-react';
import { useCrewRoster } from '@/lib/crewRoster';
import {
  fetchActiveJobsForPicker,
  fetchTodayEntries,
  fetchTimeEntries,
} from '@/lib/timeEntriesData';

export default function CrewClockPage() {
  const { t } = useTranslation();
  // ── Live crew roster (active-only) — sourced from settings.crew_rates,
  // so any employee added in Settings is immediately clockable here.
  const { roster: activeCrew, loading: rosterLoading } = useCrewRoster({
    activeOnly: true,
  });
  const [jobs, setJobs] = useState([]);
  const [today, setToday] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  // Bumped on any clock action to force history/weekly to re-fetch.
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [jobsData, todayData, allData] = await Promise.all([
        fetchActiveJobsForPicker(),
        fetchTodayEntries(),
        fetchTimeEntries({}),
      ]);
      setJobs(jobsData);
      setToday(todayData);
      setAllEntries(allData);
    } catch (e) {
      setError(e?.message || 'Failed to load crew clock data.');
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleClockChange = async () => {
    setRefreshKey((k) => k + 1);
    await load({ silent: true });
  };

  return (
    <div data-testid="crew-clock-page">
      <PageHeader
        eyebrow={t('crewClock.eyebrow')}
        title={t('crewClock.title')}
        subtitle={t('crewClock.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => load({ silent: true })}
            disabled={loading || refreshing}
            className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
            data-testid="clock-refresh-button"
          >
            <RefreshCw
              size={14}
              className={`mr-2 ${refreshing ? 'animate-spin' : ''}`}
            />
            {t('common.refresh')}
          </Button>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="clock-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      {/* Clock cards — driven by the live, active-only roster */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {rosterLoading && activeCrew.length === 0 ? (
          <div className="col-span-full text-sm text-muted-foreground py-4 text-center">
            {t('crewClock.rosterLoading')}
          </div>
        ) : activeCrew.length === 0 ? (
          <div
            className="col-span-full border border-dashed border-border rounded-md px-4 py-8 text-center bg-[hsl(var(--muted))]/40"
            data-testid="clock-empty-roster"
          >
            <Users
              size={22}
              className="mx-auto mb-2 text-muted-foreground"
            />
            <div className="text-sm font-semibold text-foreground">
              {t('crewClock.emptyRosterTitle')}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {t('crewClock.emptyRosterHint')}
            </div>
          </div>
        ) : (
          activeCrew.map((c) => (
            <ClockCard
              key={c.id}
              crew={c}
              jobs={jobs}
              onChange={handleClockChange}
            />
          ))
        )}
      </div>

      {/* Summaries row */}
      <div className="mt-5 sm:mt-6 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <TodayLog entries={today} jobs={jobs} onChange={handleClockChange} />
        </div>
        <div>
          <WeeklySummary refreshKey={refreshKey} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <HistoryTable refreshKey={refreshKey} jobs={jobs} onChange={handleClockChange} />
        </div>
        <div>
          <JobLaborSummary entries={allEntries} />
        </div>
      </div>
    </div>
  );
}
